import React, { useContext, useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import SidebarLayout from "../components/shared/SidebarLayout";
import AuthContext from "../context/AuthContext";
import { apiGet } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";

const COMMISSION_PERCENT = 3;
const cellFont = { fontSize: "0.8571428571428571rem" };

const Referral = () => {
  const { user } = useContext(AuthContext);
  const profile = user?.user;
  const referralCode = profile?.referalCode || profile?.username || "";

  const today = moment().format("YYYY-MM-DD");
  const monthStart = moment().startOf("month").format("YYYY-MM-DD");

  const [filter, setFilter] = useState({
    page: 1,
    limit: 100,
    fromPeriod: monthStart,
    toPeriod: today,
  });
  const [summary, setSummary] = useState({
    totalBonusEarn: 0,
    totalPlayerPL: 0,
  });
  const [listing, setListing] = useState([]);

  const getReferralData = async () => {
    try {
      const { data } = await apiGet(apiPath.referralReport, filter);
      if (data?.success) {
        const results = data.results || [];
        setListing(results);

        const totals = results.reduce(
          (acc, item) => {
            acc.totalBonusEarn += Number(item?.earnd || 0);
            acc.totalPlayerPL += Number(item?.playerPL || 0);
            return acc;
          },
          { totalBonusEarn: 0, totalPlayerPL: 0 },
        );
        setSummary(totals);
      }
    } catch (error) {
      console.error("Referral report error:", error);
    }
  };

  useEffect(() => {
    getReferralData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(referralCode)
      .then(() => toast.success("Copied to clipboard"))
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Refer a friend",
          text: "Join using my referral code and start playing!",
          url: `${window.location.origin}/register?referral_code=${referralCode}`,
        });
      } catch (error) {
        // user cancelled share
      }
    } else {
      toast.info("Sharing is not supported on this device");
    }
  };

  const ongoingCommission = Math.abs(
    (COMMISSION_PERCENT / 100) * summary.totalPlayerPL,
  ).toFixed(2);

  const renderPL = (value) => {
    const num = Number(value || 0);
    if (num === 0) return "0.00";
    return (
      <span style={{ color: num > 0 ? "green" : "red" }}>
        {Math.abs(num).toFixed(2)}
      </span>
    );
  };

  return (
    <SidebarLayout heading={"Referral"}>
      <div className="p-3 inner-sidebar-content">
        {/* Info + referral code */}
        <Table className="align-middle bg-white my-profile">
          <tbody>
            <tr>
              <td colSpan="3" style={cellFont}>
                Every time your referred friend makes a loss, you earn a weekly{" "}
                {COMMISSION_PERCENT}% commission. It is added to your main
                balance every Monday.
              </td>
            </tr>
            <tr>
              <td width="30%">Referral Code</td>
              <td width="40%">
                <strong>{referralCode}</strong>
              </td>
              <td width="30%" className="text-right">
                <Button
                  onClick={copyToClipboard}
                  className="theme-btn py-0 px-2"
                  style={cellFont}
                >
                  {/* <i className="icon-edit"></i> */}
                  <span style={{ fontWeight: "400", marginLeft: "5px" }}>
                    Copy
                  </span>
                </Button>
              </td>
            </tr>
            <tr>
              <td width="30%">Ongoing P&amp;L</td>
              <td width="70%" colSpan="2">
                {renderPL(summary.totalPlayerPL)}
              </td>
            </tr>
            <tr>
              <td width="30%">Ongoing Commission</td>
              <td width="40%">{ongoingCommission}</td>
              <td width="30%" className="text-right">
                <Button
                  onClick={handleShare}
                  className="theme-btn py-0 px-2"
                  style={cellFont}
                >
                  <span style={{ fontWeight: "400" }}>Share</span>
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>

        {/* Date filter */}
        <div className="d-flex align-items-center justify-content-between my-3">
          <input
            type="date"
            className="form-control"
            style={{ width: "48%" }}
            value={filter.fromPeriod}
            max={today}
            onChange={(e) =>
              setFilter({ ...filter, fromPeriod: e.target.value })
            }
          />
          <input
            type="date"
            className="form-control"
            style={{ width: "48%" }}
            value={filter.toPeriod}
            min={filter.fromPeriod}
            max={today}
            onChange={(e) => setFilter({ ...filter, toPeriod: e.target.value })}
          />
        </div>

        {/* Referral list */}
        {listing.length > 0 ? (
          listing.map((item, index) => (
            <Table
              key={item?._id || item?.username || index}
              style={{ borderRadius: ".5rem" }}
              bordered
              className="overflow-hidden bg-white"
            >
              <thead>
                <tr>
                  <th
                    colSpan="4"
                    style={{
                      fontWeight: "400",
                      fontSize: ".8571428571428571rem",
                      padding: ".3rem .5rem",
                    }}
                  >
                    {item?.username}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td width="40%">Bonus Earned</td>
                  <td width="60%">{Number(item?.earnd || 0).toFixed(2)}</td>
                </tr>
                <tr>
                  <td width="40%">P&amp;L</td>
                  <td width="60%">{renderPL(item?.playerPL)}</td>
                </tr>
              </tbody>
            </Table>
          ))
        ) : (
          <div className="text-center bg-white p-3 rounded">
            No referral data found
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Referral;
